<script setup>
import { ref, computed, onMounted } from 'vue'
import { wTrans, isLoaded } from 'laravel-vue-i18n'
import vueDebounce from 'vue-debounce'
import helpers from '../../tools/helpers'
import DataTable from 'datatables.net-vue3'
import DataTablesLib from 'datatables.net-bs5'

DataTable.use(DataTablesLib)

const vDebounce = vueDebounce({ lock: true })
const PAGE_LENGTH = 10
const currentUrl = helpers.parseUrl()
const preserveFiltering = currentUrl.segments.indexOf('player') !== -1
const emit = defineEmits(['changeMode'])
const {
    title,
    subtitle,
    teamId,
    showTournaments,
    showFree,
    paging,
    showTournamentTypeFilter,
    showSearch,
    mode,
    shortStats
} = defineProps({
    title: {
        type: String,
        required: true,
    },
    subtitle: {
        type: String,
        required: false,
    },
    teamId: {
        type: Number,
        default: null
    },
    showTournaments: {
        type: true,
        default: false,
    },
    showFree: {
        type: Boolean,
        default: false
    },
    paging: {
        type: Boolean,
        default: false,
    },
    showTournamentTypeFilter: {
        type: Boolean,
        default: false,
    },
    showSearch: {
        type: Boolean,
        default: false,
    },
    mode: {
        type: String,
        default: "all",
    },
    shortStats: {
        type: Boolean,
        default: false
    },
})
const defaultValues = {
    position: 'all',
    gameType: 'all',
    tournament: 'all',
    tournamentTier: 'all',
    tournamentType: 'all',
    search: '',
    displayStart: 0,
    fieldOrderColumn: shortStats ? 6 : 7,
    goalieOrderColumn: shortStats ? 9 : 24,
    orderDirection: 'desc',
}
const urlQueryParamStrings = {
    position: 'ppf',
    gameType: 'pgtf',
    tournament: 'ptf',
    tournamentTier: 'pttrf',
    tournamentType: 'pttf',
    search: 'ps',
    displayStart: 'pds',
    fieldOrderColumn: 'poc',
    goalieOrderColumn: 'poc',
    orderDirection: 'pod',
}
const positionFilter = ref(setParamStartValue('position'))
const gamesTypeFilter = ref(setParamStartValue('gameType'))
const tournamentFilter = ref(setParamStartValue('tournament', true))
const tournamentTierFilter = ref(setParamStartValue('tournamentTier'))
const tournamentTypeFilter = ref(
    preserveFiltering && currentUrl.params && currentUrl.params[urlQueryParamStrings.tournamentType]
        ? currentUrl.params[urlQueryParamStrings.tournamentType]
        : mode
)
const searchFilter = ref(setParamStartValue('search'))
const displayStart = ref(setParamStartValue('displayStart'))
let dt
const teamsPlayersStatsTable = ref()
const tournaments = ref([])

const isGoalie = computed(() => {
    return positionFilter.value === 'goalie'
})
const filteredTournaments = computed(() => {
    let filtered = tournaments.value

    if (tournamentTypeFilter.value !== 'all') {
        filtered = filtered.filter(tournament => tournament.mode === tournamentTypeFilter.value)
    }

    if (tournamentTierFilter.value !== 'all') {
        filtered = filtered.filter(tournament => tournament.tier === tournamentTierFilter.value)
    }

    return filtered
})

const order = ref(
    isGoalie.value
        ? [[setParamStartValue('goalieOrderColumn'), setParamStartValue('orderDirection')]]
        : [[setParamStartValue('fieldOrderColumn'), setParamStartValue('orderDirection')]]
)

// switchBestPlayers()

const ajax = {
    url: route('ajax.players'),
    data: function (d) {
        d.length = PAGE_LENGTH
        if (positionFilter.value !== 'all') {
            d.position = positionFilter.value
        }
        if (teamId) {
            d.team_id = teamId
        }
        if (gamesTypeFilter.value !== 'all') {
            d.games_type = gamesTypeFilter.value
        }
        if (tournamentFilter.value !== 'all') {
            d.tournament_id = tournamentFilter.value
        }
        if (tournamentTierFilter.value !== 'all') {
            d.tier = tournamentTierFilter.value
        }
        if (tournamentTypeFilter.value !== 'all') {
            d.tournament_type = tournamentTypeFilter.value
        }
        if (searchFilter.value && searchFilter.value.length > 3) {
            d.search_players = searchFilter.value
        }
    },
}
const columnsSettings = helpers.playerTableColumns(() => positionFilter.value, isGoalie.value)
if (shortStats) {
    columnsSettings.assists.className += ' rounded-end'
    columnsSettings.goals_against.className += ' rounded-end'
} else {
    columnsSettings.hits.className += ' rounded-end'
    columnsSettings.shootouts.className += ' rounded-end'
}
const columns = shortStats
    ? [
        columnsSettings.index, //Порядковый 0
        columnsSettings.number, //Номер игрока 1
        columnsSettings.avatar, //Аватар 2
        columnsSettings.name, //Имя 3
        columnsSettings.position, //Позиция 4

        columnsSettings.games,//GP 5

        columnsSettings.points,//P 6
        columnsSettings.goals,//G 7
        columnsSettings.assists,//A 8

        columnsSettings.wins,//W 9
        columnsSettings.loses,// L 10
        columnsSettings.goals_against,// GA 11
    ] : [
        columnsSettings.index, //Порядковый 0
        columnsSettings.number, //Номер игрока 1
        columnsSettings.avatar, //Аватар 2
        columnsSettings.platform, //Платформа 3
        columnsSettings.name, //Имя 4
        columnsSettings.position, //Позиция 5

        columnsSettings.games,//GP 6

        columnsSettings.points,//P 7
        columnsSettings.goals,//G 8
        columnsSettings.assists,//A 9
        columnsSettings.plus_minus,//+/- 10
        columnsSettings.shots,//S 11
        columnsSettings.shots_percent,//S% 12
        columnsSettings.faceoff_percent,//FO% 13
        columnsSettings.passes_percent,//P% 14
        columnsSettings.takeaways,//TA 15
        columnsSettings.interceptions,//INT 16
        columnsSettings.giveaways,//TA 17
        columnsSettings.blocked_shots,//BS 18
        columnsSettings.power_play_goals,//PPG 19
        columnsSettings.shorthanded_goals,//SHG 20
        columnsSettings.penalty_minutes,//PIM 21
        columnsSettings.game_winning_goals,//GWG 22
        columnsSettings.hits,//Hits 23

        columnsSettings.wins,//W 24
        columnsSettings.loses,// L 25
        columnsSettings.saves_percent,// SV% 246
        columnsSettings.saves,// Svs 27
        columnsSettings.goals_against,// GA 28
        columnsSettings.goal_against_per_game,// GAA 29
        columnsSettings.shootouts,// SO 30
    ]
const options = {
    language: {
        url: '/dataTables.ru.json'
    },
    responsive: true,
    scrollX: true,
    displayStart: displayStart.value,
    pageLength: PAGE_LENGTH,
    lengthChange: false,
    layout: {
        bottomEnd: {
            paging: {
                firstLast: false,
            },
        },
    },
    info: false,
    serverSide: true,
    order: order.value,
    search: {
        return: true,
    },
    paging: paging,
    searching: false,
    dataSrc: 'data',
    createdRow: createdRow,
}

const tableTrans = {
    player: wTrans('player.player'),
    games: wTrans('player.stats.games'),
    points: wTrans('player.stats.points'),
    goals: wTrans('player.stats.goals'),
    assists: wTrans('player.stats.assists'),
    plus_minus: wTrans('player.stats.plus_minus'),
    shots: wTrans('player.stats.shots'),
    shot_percent: wTrans('player.stats.shot_percent'),
    faceoff_percent: wTrans('player.stats.faceoff_percent'),
    passes_percent: wTrans('player.stats.passes_percent'),
    takeaways: wTrans('player.stats.takeaways'),
    interceptions: wTrans('player.stats.interceptions'),
    giveaways: wTrans('player.stats.giveaways'),
    blocked_shots: wTrans('player.stats.blocked_shots'),
    powerplay_goals: wTrans('player.stats.powerplay_goals'),
    shorthanded_goals: wTrans('player.stats.shorthanded_goals'),
    penalties: wTrans('player.stats.penalties'),
    game_winning_goals: wTrans('player.stats.game_winning_goals'),
    hits: wTrans('player.stats.hits'),
    wins: wTrans('player.stats.wins'),
    loses: wTrans('player.stats.loses'),
    saves_percent: wTrans('player.stats.saves_percent'),
    saves: wTrans('player.stats.saves'),
    goal_against: wTrans('player.stats.goal_against'),
    goal_against_per_game: wTrans('player.stats.goal_against_per_game'),
    shootouts: wTrans('player.stats.shootouts'),
}

onMounted(function () {
    if (showTournaments) {
        fetch(route('api.group.tournaments'))
            .then(response => response.json())
            .then(data => {
                tournaments.value = data.data.tournaments;
            });
    }

    dt = teamsPlayersStatsTable.value.dt
    const interval = setInterval(() => {
        if (!isLoaded()) {
            return
        }

        if (shortStats) {
            dt.column(3).title(tableTrans.player.value)
            dt.column(5).header().setAttribute('title', tableTrans.games.value)
            dt.column(6).header().setAttribute('title', tableTrans.points.value)
            dt.column(7).header().setAttribute('title', tableTrans.goals.value)
            dt.column(8).header().setAttribute('title', tableTrans.assists.value)
            dt.column(9).header().setAttribute('title', tableTrans.wins.value)
            dt.column(10).header().setAttribute('title', tableTrans.loses.value)
            dt.column(11).header().setAttribute('title', tableTrans.goals_against.value)
        } else {
            dt.column(4).title(tableTrans.player.value)
            dt.column(6).header().setAttribute('title', tableTrans.games.value)
            dt.column(7).header().setAttribute('title', tableTrans.points.value)
            dt.column(8).header().setAttribute('title', tableTrans.goals.value)
            dt.column(9).header().setAttribute('title', tableTrans.assists.value)
            dt.column(10).header().setAttribute('title', tableTrans.plus_minus.value)
            dt.column(11).header().setAttribute('title', tableTrans.shots.value)
            dt.column(12).header().setAttribute('title', tableTrans.shot_percent.value)
            dt.column(13).header().setAttribute('title', tableTrans.faceoff_percent.value)
            dt.column(14).header().setAttribute('title', tableTrans.passes_percent.value)
            dt.column(15).header().setAttribute('title', tableTrans.takeaways.value)
            dt.column(16).header().setAttribute('title', tableTrans.interceptions.value)
            dt.column(17).header().setAttribute('title', tableTrans.giveaways.value)
            dt.column(18).header().setAttribute('title', tableTrans.blocked_shots.value)
            dt.column(19).header().setAttribute('title', tableTrans.powerplay_goals.value)
            dt.column(20).header().setAttribute('title', tableTrans.shorthanded_goals.value)
            dt.column(21).header().setAttribute('title', tableTrans.penalties.value)
            dt.column(22).header().setAttribute('title', tableTrans.game_winning_goals.value)
            dt.column(23).header().setAttribute('title', tableTrans.hits.value)
            dt.column(24).header().setAttribute('title', tableTrans.wins.value)
            dt.column(25).header().setAttribute('title', tableTrans.loses.value)
            dt.column(26).header().setAttribute('title', tableTrans.saves_percent.value)
            dt.column(27).header().setAttribute('title', tableTrans.saves.value)
            dt.column(28).header().setAttribute('title', tableTrans.goal_against.value)
            dt.column(29).header().setAttribute('title', tableTrans.goal_against_per_game.value)
            dt.column(30).header().setAttribute('title', tableTrans.shootouts.value)
        }
        clearInterval(interval)
    }, 500)
})

function createdRow(row) {
    row.classList.add("bg-dark")
}

function onPage() {
    const info = dt.page.info()
    const displayStart = info.page * PAGE_LENGTH
    console.log(`[TEAM PLAYERS STATS] Change display start to ${displayStart}`)
    helpers.insertURLQueryParam(urlQueryParamStrings.displayStart, displayStart)

    //todo
    // $([document.documentElement, document.body]).animate({
    //     scrollTop: $playerStatsBlock.find('.playersStatsTable').offset().top - 160
    // }, 0)
}

function onOrder() {
    const order = dt.order()
    console.log(`[TEAM PLAYERS STATS] Change order to '${order[0][0]}' '${order[0][1]}'`)
    // This will show: "Ordering on column 1 (asc)", for example
    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.fieldOrderColumn, order[0][0])
    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.orderDirection, order[0][1])
    setDefaultValue(displayStart, 'displayStart')
}

function onClickPlayerPosition(value) {
    console.log(`[TEAM PLAYERS STATS] Change player position to ${value}`)
    positionFilter.value = value
    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.position, positionFilter.value)
    setDefaultValue(displayStart, 'displayStart')
    setDefaultOrderValue()

    const fieldColumns = shortStats
        ? [6, 7, 8]
        : [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
    for (const index of fieldColumns) {
        dt.column(index).visible(!isGoalie.value)
    }
    const goalieColumns = shortStats
        ? [9, 10, 11]
        : [24, 25, 26, 27, 28, 29, 30]
    for (const index of goalieColumns) {
        dt.column(index).visible(isGoalie.value)
    }

    dt.order([[
        isGoalie.value ? defaultValues.goalieOrderColumn : defaultValues.fieldOrderColumn,
        defaultValues.orderDirection
    ]]).rows().remove().draw()
}

function onChangeGameType() {
    console.log(`[TEAM PLAYERS STATS] Change games type to ${gamesTypeFilter.value}`)

    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.gameType, gamesTypeFilter.value)
    setDefaultValue(displayStart, 'displayStart')
    setDefaultOrderValue()

    dt.draw()
}

function onChangeTournament() {
    console.log(`[TEAM PLAYERS STATS] Change tournament to ${tournamentFilter.value}`)

    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.tournament, tournamentFilter.value)
    setDefaultValue(displayStart, 'displayStart')
    setDefaultOrderValue()

    dt.draw()
}

function onChangeTournamentTier() {
    console.log(`[TEAM PLAYERS STATS] Change tournament tier to ${tournamentTierFilter.value}`)

    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.tournamentTier, tournamentTierFilter.value)
    setDefaultValue(tournamentFilter, 'tournament')
    setDefaultValue(displayStart, 'displayStart')
    setDefaultOrderValue()

    dt.draw()
}

function onChangeTournamentType() {
    console.log(`[TEAM PLAYERS STATS] Change tournament type to ${tournamentTypeFilter.value}`)

    emit('changeMode', tournamentTypeFilter.value)

    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.tournamentType, tournamentTypeFilter.value)
    setDefaultValue(tournamentFilter, 'tournament')
    setDefaultValue(displayStart, 'displayStart')
    setDefaultOrderValue()

    dt.draw()
}

function onKeyUpSearch(value) {
    console.log(`[TEAM PLAYERS STATS] Search player by "${value}"`)

    searchFilter.value = value

    preserveFiltering && helpers.insertURLQueryParam(urlQueryParamStrings.search, searchFilter.value)
    setDefaultValue(displayStart, 'displayStart')

    dt.draw()
}

function setDefaultValue(param, paramKey) {
    console.log(`[TEAM PLAYERS STATS] Set '${paramKey}' to default`)

    param.value = defaultValues[paramKey]
    TRNMNT_helpers.deleteQueryParam(urlQueryParamStrings[paramKey])
}

function setDefaultOrderValue() {
    console.log(`[TEAM PLAYERS STATS] Set order to default`)

    order.value = isGoalie.value
        ? [[setParamStartValue('goalieOrderColumn'), setParamStartValue('orderDirection')]]
        : [[setParamStartValue('fieldOrderColumn'), setParamStartValue('orderDirection')]]
    TRNMNT_helpers.deleteQueryParam(urlQueryParamStrings[isGoalie.value ? 'goalieOrderColumn' : 'fieldOrderColumn'])
    TRNMNT_helpers.deleteQueryParam(urlQueryParamStrings.orderDirection)
}

function setParamStartValue(param, numeric = false) {
    return currentUrl.params && currentUrl.params[urlQueryParamStrings[param]]
        ? (numeric ? +currentUrl.params[urlQueryParamStrings[param]] : currentUrl.params[urlQueryParamStrings[param]])
        : defaultValues[param]
}

</script>

<template>
<div v-if="title" class="mt-4">
    <h3>{{ title }}</h3>
    <div v-if="subtitle" class="small">{{ subtitle }}</div>
</div>

<ul
    class="nav nav-tabs justify-content-start justify-content-lg-end mb-4 fs-7 fs-lg-4 fw-bold"
    role="tablist"
>
    <li class="nav-item" role="presentation">
        <button
            @click="onClickPlayerPosition('all')"
            :class="['nav-link', positionFilter === 'all' ? 'active' : '']"
            id="all-tab"
            data-bs-toggle="tab"
            data-bs-target="#all-tab-pane"
            type="button"
            role="tab"
            aria-controls="all-tab-pane"
            aria-selected="true"
        >
            {{ $t('player.all') }}
        </button>
    </li>
    <li class="nav-item" role="presentation">
        <button
            @click="onClickPlayerPosition('offense')"
            :class="['nav-link', positionFilter === 'offense' ? 'active' : '']"
            id="offense-tab"
            data-bs-toggle="tab"
            data-bs-target="#offense-tab-pane"
            type="button"
            role="tab"
            aria-controls="offense-tab-pane"
            aria-selected="false"
        >
            {{ $t('player.offense') }}
        </button>
    </li>
    <li class="nav-item" role="presentation">
        <button
            @click="onClickPlayerPosition('defense')"
            :class="['nav-link', positionFilter === 'defense' ? 'active' : '']"
            id="defense-tab"
            data-bs-toggle="tab"
            data-bs-target="#defense-tab-pane"
            type="button"
            role="tab"
            aria-controls="defense-tab-pane"
            aria-selected="false"
        >
            {{ $t('player.defense') }}
        </button>
    </li>
    <li class="nav-item" role="presentation">
        <button
            @click="onClickPlayerPosition('goalie')"
            :class="['nav-link', positionFilter === 'goalie' ? 'active' : '']"
            id="goalie-tab"
            data-bs-toggle="tab"
            data-bs-target="#goalie-tab-pane"
            data-position="goalie"
            type="button"
            role="tab"
            aria-controls="goalie-tab-pane"
            aria-selected="false"
        >
            {{ $t('player.goalies') }}
        </button>
    </li>
    <li v-if="showFree" lass="nav-item" role="presentation">
        <button
            @click="onClickPlayerPosition('free')"
            :class="['nav-link', positionFilter === 'free' ? 'active' : '']"
            id="free-tab"
            data-bs-toggle="tab"
            data-bs-target="#free-tab-pane"
            type="button"
            role="tab"
            aria-controls="free-tab-pane"
            aria-selected="false"
        >
            {{ $t('common.players_free') }}
        </button>
    </li>
</ul>

<div class="teams-filter justify-content-end mt-2">
    <div v-if="showSearch" class="match-filter__select input-group w-auto">
        <span class="input-group-text bg-dark text-white" id="basic-addon1">
            <i class="fa fa-search"></i>
        </span>
        <input
            v-model="searchFilter"
            v-debounce:500ms="onKeyUpSearch"
            class="form-control form-control-dark w-auto"
            :placeholder="$t('common.search')"
        >
    </div>
    <select
        v-if="showTournaments"
        v-model="tournamentFilter"
        @change.prevent="onChangeTournament"
        class="match-filter__select form-select form-control-dark w-auto"
    >
        <option value="all">
            {{ !showFree ? $t('team.roster_active') : $t('common.tournaments_all') }}
        </option>
        <option
            v-for="tournament in filteredTournaments"
            :key="tournament.id"
            :value="tournament.id"
        >{{ tournament.title }}</option>
    </select>
    <select
        v-else
        v-model="tournamentTierFilter"
        @change.prevent="onChangeTournamentTier"
        class="match-filter__select form-select form-control-dark w-auto"
    >
        <option value="all">{{ $t('common.tournaments_all') }}</option>
        <option value="1">{{ $t('tournament.tier_1') }}</option>
        <option value="2">{{ $t('tournament.tier_2')  }}</option>
    </select>
    <!-- {{--Режимы игры--}} -->
    <select
        v-if="showTournamentTypeFilter"
        v-model="tournamentTypeFilter"
        @change.prevent="onChangeTournamentType"
        class="match-filter__select form-select form-control-dark w-auto"
    >
        <option value="all">{{ $t('common.tournament_types_all') }}</option>
        <option value="6">6x6</option>
        <option value="3">3x3</option>
    </select>
    <!-- {{--Конец режимов игры--}} -->
    <!-- {{--Тип игры--}} -->
    <select
        v-model="gamesTypeFilter"
        @change.prevent="onChangeGameType"
        class="match-filter__select form-select form-control-dark w-auto"
    >
        <option value="all">{{ $t('common.games_all') }}</option>
        <option value="playoff">{{ $t('common.games_playoff') }}</option>
        <option value="regular">{{ $t('common.games_regular') }}</option>
    </select>
    <!-- {{--Конец типа игры--}} -->
</div>

<data-table
    :ajax="ajax"
    :columns="columns"
    :options="options"
    ref="teamsPlayersStatsTable"
    class="table table-borderless"
    @page="onPage"
    @order="onOrder"
>
    <thead>
    <tr>
        <th class="stats-data-platform"></th>
        <th class="stats-data-platform">#</th>
        <th class="stats-data-avatar"></th>
        <th v-if="!shortStats" class="stats-data-platform"></th>
        <th class="title-row"></th>
        <th class="player-stats-position"></th>

        <th :title="$t('player.stats.games')" class="fw-bold stats-data">GP</th>

        <th :title="$t('player.stats.points')" class="fw-bold stats-data">P</th>
        <th :title="$t('player.stats.goals')" class="fw-bold stats-data">G</th>
        <th :title="$t('player.stats.assists')" class="fw-bold stats-data">A</th>
        <th v-if="!shortStats" :title="$t('player.stats.plus_minus')" class="fw-bold stats-data">+/-</th>
        <th v-if="!shortStats" :title="$t('player.stats.shots')" class="fw-bold stats-data">S</th>
        <th v-if="!shortStats" :title="$t('player.stats.shot_percent')" class="fw-bold stats-data">S%</th>
        <th v-if="!shortStats" :title="$t('player.stats.faceoff_percent')" class="fw-bold stats-data">FO%</th>
        <th v-if="!shortStats" :title="$t('player.stats.passes_percent')" class="fw-bold stats-data">P%</th>
        <th v-if="!shortStats" :title="$t('player.stats.takeaways')" class="fw-bold stats-data">TA</th>
        <th v-if="!shortStats" :title="$t('player.stats.interceptions')" class="fw-bold stats-data">INT</th>
        <th v-if="!shortStats" :title="$t('player.stats.giveaways')" class="fw-bold stats-data">GA</th>
        <th v-if="!shortStats" :title="$t('player.stats.blocked_shots')" class="fw-bold stats-data">BS</th>
        <th v-if="!shortStats" :title="$t('player.stats.powerplay_goals')" class="fw-bold stats-data">PPG</th>
        <th v-if="!shortStats" :title="$t('player.stats.shorthanded_goals')" class="fw-bold stats-data">SHG</th>
        <th v-if="!shortStats" :title="$t('player.stats.penalties')" class="fw-bold stats-data">PIM</th>
        <th v-if="!shortStats" :title="$t('player.stats.game_winning_goals')" class="fw-bold stats-data">GWG</th>
        <th v-if="!shortStats" :title="$t('player.stats.hits')" class="fw-bold stats-data">Hits</th>

        <th :title="$t('player.stats.wins')" class="fw-bold stats-data">W</th>
        <th :title="$t('player.stats.loses')" class="fw-bold stats-data">L</th>
        <th v-if="!shortStats" :title="$t('player.stats.saves_percent')" class="fw-bold stats-data">SV%</th>
        <th v-if="!shortStats" :title="$t('player.stats.saves')" class="fw-bold stats-data">Svs</th>
        <th :title="$t('player.stats.goal_against')" class="fw-bold stats-data">GA</th>
        <th v-if="!shortStats" :title="$t('player.stats.goal_against_per_game')" class="fw-bold stats-data">GAA</th>
        <th v-if="!shortStats" :title="$t('player.stats.shootouts')" class="fw-bold stats-data">SO</th>
    </tr>
    </thead>
</data-table>
</template>
